import { useSendOnboarding } from '@nxsys/hooks/api';
import useInviteOnboardingSocket from '@nxsys/hooks/useInviteOnboardingSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import {
  SendOnboardingEmployee,
  SendOnboardingError,
  SendOnboardingJobResult,
} from '@nxsys/types/api/employee';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { showMessage } from '@nxsys/utils/message';
import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { find, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Invites employees to onboarding from an employee listing and reports how
// each invite ended. `refetchList` reloads the listing once the invite is done.
const useInviteOnboarding = (refetchList: () => void) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: AppState) => state.appStore);

  // An invite opens a prospect for the employee, so the Prospects badge in
  // the sidebar is fetched again along with the list.
  const refetch = () => {
    refetchList();
    dispatch(prospectActions.getProspectPendingCount());
  };

  // A queued invite keeps the screen loading until the job reports back, or
  // until the timeout gives the screen back if the report never comes.
  const [isInviteOnboardingQueued, setIsInviteOnboardingQueued] =
    useState(false);
  const inviteOnboardingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const { run: sendOnboarding, loading: isSendOnboardingLoading } =
    useSendOnboarding();

  const employeeColumns: ColumnsType<SendOnboardingEmployee> = [
    { title: 'Employee ID', dataIndex: 'id', key: 'id', width: 130 },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 220,
      render: (fullName?: string) => fullName || '-',
    },
  ];

  // An outer border only; the last row drops its own bottom line so the
  // border is not doubled there.
  const resultTableBorder =
    'overflow-hidden rounded-lg border border-solid border-[#f0f0f0] [&_tr:last-child>td]:!border-b-0';

  // The invite refuses some employees without failing the request, listing
  // them in `errors`, so success is only reported for the ones not there.
  const reportSendOnboardingResult = (
    sent: SendOnboardingEmployee[],
    errors: SendOnboardingError[],
  ) => {
    if (isEmpty(errors)) {
      showMessage({ type: 'success', content: 'Onboarding invitation sent' });
      return;
    }
    if (sent.length + errors.length === 1) {
      showMessage({
        type: 'error',
        content: `Onboarding not sent: ${errors[0].error}`,
      });
      return;
    }
    Modal.warning({
      title: isEmpty(sent)
        ? 'Onboarding not sent'
        : 'Onboarding sent to some employees',
      width: 800,
      content: (
        <div className="flex flex-col gap-4">
          {!isEmpty(sent) && (
            <div>
              <div className="mb-2 font-medium">Sent ({sent.length})</div>
              <div className={resultTableBorder}>
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 240 }}
                  dataSource={sent}
                  columns={employeeColumns}
                />
              </div>
            </div>
          )}
          <div>
            <div className="mb-2 font-medium">Not sent ({errors.length})</div>
            <div className={resultTableBorder}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 240 }}
                dataSource={errors}
                columns={[
                  ...employeeColumns,
                  { title: 'Error Message', dataIndex: 'error', key: 'error' },
                ]}
              />
            </div>
          </div>
        </div>
      ),
    });
  };

  const stopWaitingForInviteOnboarding = () => {
    clearTimeout(inviteOnboardingTimeoutRef.current);
    setIsInviteOnboardingQueued(false);
  };

  // The job invites one employee at a time, so it is given longer the more
  // there are: 30s plus 3s each, at most 5 minutes.
  const waitForInviteOnboarding = (count: number) => {
    clearTimeout(inviteOnboardingTimeoutRef.current);
    setIsInviteOnboardingQueued(true);
    inviteOnboardingTimeoutRef.current = setTimeout(
      () => {
        setIsInviteOnboardingQueued(false);
        showMessage({
          type: 'info',
          content: messages['common.inviteOnboardingPending'],
        });
        refetch();
      },
      Math.min(30_000 + count * 3_000, 300_000),
    );
  };

  useEffect(() => () => clearTimeout(inviteOnboardingTimeoutRef.current), []);

  // Several employees are invited by a background job, which reports back
  // here once every invite has gone out or been refused.
  useInviteOnboardingSocket({
    eventName: `invite-onboarding-${user?.id}`,
    onSuccess: (data: SendOnboardingJobResult) => {
      stopWaitingForInviteOnboarding();
      reportSendOnboardingResult(data?.sent ?? [], data?.errors ?? []);
      refetch();
    },
    onError: (error) => {
      stopWaitingForInviteOnboarding();
      showMessage({
        type: 'error',
        content: error || 'Invite onboarding failed',
      });
      refetch();
    },
  });

  // The list is fetched again however the invite ends: a failed one still
  // changes the employee (in onboarding, with its error to show). A queued
  // invite is fetched again by the socket handler above, once the job is done.
  const sendOnboardingTo = async (employees: EmployeeRecordType[]) => {
    let queued = false;
    try {
      const res = await sendOnboarding({ ids: employees.map((e) => e.id) });
      queued = !!res?.queued;
      if (queued) {
        waitForInviteOnboarding(employees.length);
        showMessage({
          type: 'info',
          content: messages['common.inviteOnboardingPending'],
        });
        return;
      }
      const errors = (res?.errors ?? []).map((e) => ({
        ...e,
        fullName: find(employees, { id: e.id })?.fullName,
      }));
      const failedIds = errors.map((e) => e.id);
      reportSendOnboardingResult(
        employees.filter((e) => !failedIds.includes(e.id)),
        errors,
      );
    } catch {
      // useSendOnboarding already shows the error
    } finally {
      if (!queued) refetch();
    }
  };

  // Re-inviting closes the employee's current journey and starts a new one, so
  // it is only done once the user confirms it for those already in onboarding.
  const handleSendOnboarding = (employees: EmployeeRecordType[]) => {
    if (!employees.some((e) => e.isOnboarding)) {
      sendOnboardingTo(employees);
      return;
    }
    Modal.confirm({
      title: 'Re-invite Onboarding?',
      width: 560,
      content: (
        <div className="flex flex-col gap-3">
          <div>
            Re-inviting closes any current onboarding and starts a new one. The
            ID, share code and contract steps are asked again.
          </div>
          <div>
            Employees who already have portal access keep their current
            password. Only those without an account are emailed a new one.
          </div>
        </div>
      ),
      okText: 'Re-invite',
      onOk: () => sendOnboardingTo(employees),
    });
  };

  // Only employees whose employer has onboarding on can be invited, so the
  // rest of a selection is left out.
  const handleSendOnboardingSelected = (selectedRows: EmployeeRecordType[]) => {
    const employees = selectedRows.filter(
      (r) => r?.companyPayment?.subOnboarding,
    );
    if (isEmpty(employees)) {
      showMessage({
        type: 'warning',
        content: 'Onboarding is not enabled for the selected employees',
      });
      return;
    }
    handleSendOnboarding(employees);
  };

  return {
    handleSendOnboarding,
    handleSendOnboardingSelected,
    isInviteOnboardingLoading:
      isSendOnboardingLoading || isInviteOnboardingQueued,
  };
};

export default useInviteOnboarding;
